import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";

const SETTING_LABELS = {
  clinic: "In clinic, on the Coast",
  your_home: "In your home",
  cafe: "Coffee at a café",
  fishing: "Fishing",
  trek: "A weekend trek",
  family_camp: "A family camp",
};

const SKILL_LABELS = {
  beginner: "Beginner — never done it, keen to try",
  intermediate: "Intermediate — done it a few times",
  advanced: "Advanced — I know what I'm doing",
};

const STEPS = ["Session", "Setting", "When", "You"];

export default function Book() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [familyDone, setFamilyDone] = useState(null);
  const [giftDone, setGiftDone] = useState(null);
  const [giftInfo, setGiftInfo] = useState(null);
  const [giftError, setGiftError] = useState("");
  const [form, setForm] = useState({
    session_type: params.get("type") || "",
    setting: "",
    skill_level: "",
    qualified_to_lead: null,
    preferred_date: "",
    time_slot: "",
    karen_present: false,
    companion: false,
    name: "",
    email: "",
    phone: "",
    notes: "",
    gift_code: "",
  });

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.preferred_date) return;
    setSlotsLoading(true);
    api.get(`/availability?date=${form.preferred_date}`)
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots(null))
      .finally(() => setSlotsLoading(false));
  }, [form.preferred_date]);

  const isActivity = useMemo(
    () => config?.activity_settings?.includes(form.setting),
    [config, form.setting]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext =
    (step === 1 && form.session_type) ||
    (step === 2 && form.setting && (!isActivity || (form.skill_level && (form.skill_level !== "advanced" || form.qualified_to_lead !== null)))) ||
    (step === 3 && form.preferred_date && form.time_slot) ||
    (step === 4 && form.name && form.email);

  const checkGiftCode = async () => {
    if (!form.gift_code.trim()) return;
    setGiftError("");
    setGiftInfo(null);
    try {
      const res = await api.get(`/gifts/check/${encodeURIComponent(form.gift_code.trim())}`);
      setGiftInfo(res.data);
      set("session_type", res.data.session_type);
      toast.success(`Code covers a ${res.data.session_label} — locked in as your session type.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setGiftError(typeof d === "string" ? d : "That code isn't valid or has already been used");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        phone: form.phone || null,
        notes: form.notes || null,
        gift_code: form.gift_code || null,
        skill_level: isActivity ? form.skill_level : null,
        qualified_to_lead: isActivity && form.skill_level === "advanced" ? form.qualified_to_lead : null,
      };
      const res = await api.post("/bookings", payload);
      const { booking_id, price_aud, status } = res.data;
      if (price_aud === null) {
        setFamilyDone(res.data);
        return;
      }
      if (status === "confirmed") {
        setGiftDone(res.data);
        return;
      }
      const checkout = await api.post("/payments/checkout", {
        booking_id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something jammed. Try again.");
      setSubmitting(false);
    }
  };

  if (giftDone) {
    return (
      <div className="bg-hearth-bg min-h-screen">
        <Nav />
        <main className="pt-40 pb-24 max-w-2xl mx-auto px-6" data-testid="booking-gift-success">
          <Check className="w-10 h-10 text-hearth-ochre mb-6" strokeWidth={1.5} />
          <h1 className="font-display font-bold text-4xl text-hearth-ink">The gift just became a booking.</h1>
          <p className="mt-6 font-essay text-xl text-hearth-ink/90 leading-loose">
            Code accepted, session confirmed, nothing to pay — someone already took care of that.
            All that's left is turning up.
          </p>
          <p className="mt-4 text-hearth-muted text-sm">
            {SETTING_LABELS[form.setting]} · {form.preferred_date} · {form.time_slot}
            {form.karen_present ? " · Karen sitting in" : ""}{form.companion ? " · Bringing someone with you" : ""}
          </p>
        </main>
        <Footer config={config} />
      </div>
    );
  }

  if (familyDone) {
    return (
      <div className="bg-hearth-bg min-h-screen">
        <Nav />
        <main className="pt-40 pb-24 max-w-2xl mx-auto px-6" data-testid="booking-family-success">
          <Check className="w-10 h-10 text-hearth-ochre mb-6" strokeWidth={1.5} />
          <h1 className="font-display font-bold text-4xl text-hearth-ink">Booked in pencil.</h1>
          <p className="mt-6 font-essay text-xl text-hearth-ink/90 leading-loose">
            Family work is priced by conversation, not by checkout — so Matt will ring you himself
            to shape the day and agree a fair number before anything is locked in.
          </p>
          <p className="mt-4 text-hearth-muted text-sm">
            {SETTING_LABELS[form.setting]} · {form.preferred_date} · {form.time_slot}
            {form.karen_present ? " · Karen sitting in" : ""}{form.companion ? " · Bringing someone with you" : ""}
          </p>
        </main>
        <Footer config={config} />
      </div>
    );
  }

  return (
    <div className="bg-hearth-bg min-h-screen" data-testid="book-page">
      <Seo
        title="Book a Session | No BS Counselling"
        description="Pick a session, pick the setting — clinic, home, café, fishing, trek or family camp — and lock in a time. No forms, no fuss."
        path="/book"
      />
      <Nav />
      <main className="pt-40 pb-24 max-w-3xl mx-auto px-6">
        <p className="eyebrow mb-4">Ridiculously Easy, As Promised</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-hearth-ink">Book a session.</h1>

        <div className="mt-10 flex gap-px bg-hearth-line border border-hearth-line" data-testid="booking-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 py-3 text-center text-xs uppercase tracking-widest ${step === i + 1 ? "bg-hearth-ember text-hearth-ink" : step > i + 1 ? "bg-hearth-surface text-hearth-ochre" : "bg-hearth-surface text-hearth-muted"}`}>
              {s}
            </div>
          ))}
        </div>

        <div className="mt-12">
          {step === 1 && (
            <div className="space-y-4" data-testid="step-session">
              {config?.session_types?.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`session-type-${t.id}`}
                  onClick={() => set("session_type", t.id)}
                  className={`w-full text-left flex items-center justify-between border p-6 transition-colors ${form.session_type === t.id ? "border-hearth-ember bg-hearth-surface" : "border-hearth-line hover:border-hearth-ochre"}`}
                >
                  <span className="font-display font-bold text-xl text-hearth-ink">{t.label}</span>
                  <span className="font-display font-bold text-2xl text-hearth-ochre">
                    {t.price_aud === null ? "Let's talk" : `$${t.price_aud}`}
                  </span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-setting">
              <div className="grid sm:grid-cols-2 gap-4">
                {config?.settings?.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`setting-${s}`}
                    onClick={() => setForm((f) => ({ ...f, setting: s, skill_level: "", qualified_to_lead: null }))}
                    className={`text-left border p-6 transition-colors ${form.setting === s ? "border-hearth-ember bg-hearth-surface" : "border-hearth-line hover:border-hearth-ochre"}`}
                  >
                    <span className="font-display font-bold text-lg text-hearth-ink">{SETTING_LABELS[s]}</span>
                  </button>
                ))}
              </div>
              {isActivity && (
                <div className="mt-8 border border-hearth-line p-6" data-testid="skill-questionnaire">
                  <p className="eyebrow mb-4">So the Day Fits You</p>
                  <p className="text-hearth-muted text-sm mb-4">How experienced are you at {SETTING_LABELS[form.setting].toLowerCase()}?</p>
                  <div className="space-y-3">
                    {Object.entries(SKILL_LABELS).map(([k, label]) => (
                      <label key={k} className={`flex items-center gap-4 border p-4 cursor-pointer transition-colors ${form.skill_level === k ? "border-hearth-ember bg-hearth-bg" : "border-hearth-line hover:border-hearth-ochre"}`} data-testid={`skill-${k}`}>
                        <input type="radio" name="skill" className="accent-[#c25934]" checked={form.skill_level === k} onChange={() => set("skill_level", k)} />
                        <span className="text-sm text-hearth-ink">{label}</span>
                      </label>
                    ))}
                  </div>
                  {form.skill_level === "advanced" && (
                    <div className="mt-6" data-testid="qualified-question">
                      <p className="text-sm text-hearth-muted mb-3">Advanced — good. Are you qualified to lead the activity yourself?</p>
                      <div className="flex gap-3">
                        <button type="button" data-testid="qualified-yes" onClick={() => set("qualified_to_lead", true)} className={`px-6 py-3 text-xs uppercase tracking-widest font-semibold border transition-colors ${form.qualified_to_lead === true ? "border-hearth-ember bg-hearth-ember text-hearth-ink" : "border-hearth-line text-hearth-muted hover:border-hearth-ochre"}`}>Yes, qualified</button>
                        <button type="button" data-testid="qualified-no" onClick={() => set("qualified_to_lead", false)} className={`px-6 py-3 text-xs uppercase tracking-widest font-semibold border transition-colors ${form.qualified_to_lead === false ? "border-hearth-ember bg-hearth-ember text-hearth-ink" : "border-hearth-line text-hearth-muted hover:border-hearth-ochre"}`}>No — Matt leads</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-when">
              <label className="block text-sm text-hearth-muted mb-3">Pick a day</label>
              <input
                type="date"
                data-testid="booking-date-input"
                value={form.preferred_date}
                min={new Date(Date.now() + 86400000).toISOString().split("T")[0]}
                onChange={(e) => setForm((f) => ({ ...f, preferred_date: e.target.value, time_slot: "" }))}
                className="bg-hearth-surface border border-hearth-line px-5 py-4 text-hearth-ink focus:outline-none focus:border-hearth-ochre [color-scheme:dark]"
              />
              {slotsLoading && <p className="mt-6 text-hearth-muted text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking the diary…</p>}
              {slots && !slotsLoading && (
                <div className="mt-8 grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="slot-grid">
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      type="button"
                      disabled={!s.available}
                      data-testid={`slot-${s.time.replace(":", "")}`}
                      onClick={() => set("time_slot", s.time)}
                      className={`py-4 text-sm font-semibold tracking-widest border transition-colors ${
                        !s.available
                          ? "border-hearth-line text-hearth-muted/40 line-through cursor-not-allowed"
                          : form.time_slot === s.time
                            ? "border-hearth-ember bg-hearth-ember text-hearth-ink"
                            : "border-hearth-line text-hearth-ink hover:border-hearth-ochre"
                      }`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-details" className="space-y-6">
              {form.session_type !== "family_session" && (
                <div className="border border-hearth-line p-6" data-testid="gift-code-block">
                  <p className="eyebrow mb-3">Got a Gift Code?</p>
                  <div className="flex gap-3">
                    <input data-testid="gift-code-input" value={form.gift_code} onChange={(e) => { set("gift_code", e.target.value.toUpperCase()); setGiftInfo(null); setGiftError(""); }}
                      placeholder="FIRE-XXXX-XXXX"
                      className="flex-1 bg-hearth-bg border border-hearth-line px-5 py-3 text-sm tracking-widest text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre" />
                    <button type="button" data-testid="gift-code-check-button" onClick={checkGiftCode}
                      className="border border-hearth-line hover:border-hearth-ochre text-hearth-muted hover:text-hearth-ink text-xs font-semibold uppercase tracking-widest px-5 transition-colors">
                      Check
                    </button>
                  </div>
                  {giftInfo && <p data-testid="gift-code-valid" className="mt-3 text-hearth-ochre text-sm">Covers a {giftInfo.session_label} — payment's already handled.</p>}
                  {giftError && <p data-testid="gift-code-error" className="mt-3 text-hearth-ember text-sm">{giftError}</p>}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <button type="button" data-testid="karen-toggle" onClick={() => set("karen_present", !form.karen_present)} className={`text-left border p-6 transition-colors ${form.karen_present ? "border-hearth-ember bg-hearth-surface" : "border-hearth-line hover:border-hearth-ochre"}`}>
                  <p className="font-display font-bold text-lg text-hearth-ink">Karen sits in</p>
                  <p className="text-hearth-muted text-sm mt-2">Forty years of marriage makes a formidable team. Optional, always.</p>
                </button>
                <button type="button" data-testid="companion-toggle" onClick={() => set("companion", !form.companion)} className={`text-left border p-6 transition-colors ${form.companion ? "border-hearth-ember bg-hearth-surface" : "border-hearth-line hover:border-hearth-ochre"}`}>
                  <p className="font-display font-bold text-lg text-hearth-ink">Bringing someone</p>
                  <p className="text-hearth-muted text-sm mt-2">A mate, an advocate, a witness. Whoever helps you say it.</p>
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="booking-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="First name is plenty *" className="bg-hearth-surface border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre" />
                <input data-testid="booking-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email for the confirmation *" className="bg-hearth-surface border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre" />
              </div>
              <input data-testid="booking-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (optional — handy for treks and weather)" className="w-full bg-hearth-surface border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre" />
              <textarea data-testid="booking-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} placeholder="Anything he should know beforehand? Say as much or as little as you like." className="w-full bg-hearth-surface border border-hearth-line px-5 py-4 text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre font-essay text-lg" />
            </div>
          )}
        </div>

        <div className="mt-12 flex items-center justify-between">
          {step > 1 ? (
            <button type="button" data-testid="booking-back-button" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 text-hearth-muted hover:text-hearth-ink text-xs uppercase tracking-widest font-semibold transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : <span />}
          {step < 4 ? (
            <button type="button" data-testid="booking-next-button" disabled={!canNext} onClick={() => setStep(step + 1)} className="inline-flex items-center gap-3 bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg disabled:opacity-30 disabled:cursor-not-allowed text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button type="button" data-testid="booking-submit-button" disabled={!canNext || submitting} onClick={submit} className="inline-flex items-center gap-3 bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg disabled:opacity-30 disabled:cursor-not-allowed text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {form.session_type === "family_session" || giftInfo ? "Lock It In" : "Pay & Lock It In"}
            </button>
          )}
        </div>
        <p className="mt-6 text-hearth-muted text-xs leading-relaxed">
          Paid sessions go through Stripe — card details never touch this site. Your booking notes are seen by Matt only.
        </p>
      </main>
      <Footer config={config} />
    </div>
  );
}
